import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { User, Lock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../hooks/useTheme';

export const Login = () => {
  const [username, setUsername] = useState('admin_demo');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const { loginState } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!username || !password) {
      setError('لطفاً نام کاربری و رمز عبور را وارد کنید');
      return;
    }

    setIsLoading(true);
    
    try {
      const data = await authService.login(username, password);
      loginState(data.user);
      if (data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/customer');
      }
    } catch (err: any) {
      setError(err.message || 'خطا در برقراری ارتباط با سرور');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div dir="rtl" className="min-h-screen w-full bg-[#f0f2f5] dark:bg-slate-900 flex flex-col items-center justify-center p-6 transition-colors duration-300 font-sans">
      <AnimatePresence>
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-sm bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 md:p-10 relative border border-slate-100 dark:border-slate-700"
        >
          <div className="absolute top-4 left-4">
            <ThemeToggle isDark={isDark} toggle={toggleTheme} />
          </div>
          
          <div className="mb-8 mt-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">ورود به حساب کاربری</h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">لطفاً نام کاربری و رمز عبور خود را وارد کنید.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <User size={16} /> نام کاربری
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:text-white placeholder:text-slate-400"
                placeholder="example@domain.com"
                dir="ltr"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between items-center mb-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <Lock size={16} /> رمز عبور
                </label>
                <a href="#" className="text-xs text-blue-600 dark:text-blue-400 hover:underline">فراموشی رمز؟</a>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all dark:text-white placeholder:text-slate-400"
                placeholder="••••••••"
                dir="ltr"
              />
            </div>

            <div className="flex items-center space-x-2 space-x-reverse pt-2">
              <input type="checkbox" id="remember" className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 text-blue-600 focus:ring-blue-500" />
              <label htmlFor="remember" className="text-sm text-slate-600 dark:text-slate-400 cursor-pointer">مرا به خاطر بسپار</label>
            </div>

            {error && (
              <motion.p 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="text-red-500 dark:text-red-400 text-sm font-medium"
              >
                {error}
              </motion.p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/30 dark:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed group flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  ورود به پنل کاربری
                  <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-700/50 flex items-center justify-center space-x-1 space-x-reverse text-sm text-slate-500 dark:text-slate-400">
            <span>هنوز عضو نشده‌اید؟</span>
            <a href="#" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">ایجاد حساب جدید</a>
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="mt-8 text-slate-400 dark:text-slate-500 text-xs text-center w-full max-w-sm">
        تمامی حقوق برای شرکت توسعه فناوری محفوظ است © ۱۴۰۲
      </div>
    </div>
  );
};
